import type { LevelDef, RopeSegment, SceneId } from './types';

export const SCENE_ORDER: SceneId[] = ['pond', 'swamp', 'storm', 'ice', 'heron'];

export const SCENE_NAMES: Record<SceneId, string> = {
  pond: 'Estanque al atardecer',
  swamp: 'Pantano de noche',
  storm: 'Tormenta',
  ice: 'Estanque helado',
  heron: 'Nido de la garza',
};

/** Arcade and daily runs loop through these phases, 10% faster each loop. */
export const ARCADE_PHASES: RopeSegment[] = [
  { type: 'steady', period: 1.45, turns: 6 },
  { type: 'accel', period: 1.4, to: 1.15, turns: 8 },
  { type: 'feint', period: 1.15, turns: 6 },
  { type: 'steady', period: 1.05, turns: 6 },
  { type: 'burst', period: 1.1, turns: 8 },
  { type: 'double', period: 2.0, turns: 8 },
  { type: 'reverse', period: 1.0, turns: 6 },
  { type: 'accel', period: 1.05, to: 0.85, turns: 8 },
  { type: 'double', period: 1.7, turns: 8 },
  { type: 'feint', period: 0.95, turns: 6 },
];

export const WORLDS: { n: number; name: string; scene: SceneId }[] = [
  { n: 1, name: 'Estanque', scene: 'pond' },
  { n: 2, name: 'Pantano', scene: 'swamp' },
  { n: 3, name: 'Tormenta', scene: 'storm' },
  { n: 4, name: 'Hielo', scene: 'ice' },
];

const s = (period: number, turns = 1): RopeSegment => ({ type: 'steady', period, turns });
const a = (period: number, to: number, turns: number): RopeSegment => ({ type: 'accel', period, to, turns });
const f = (period: number, turns: number): RopeSegment => ({ type: 'feint', period, turns });
const b = (period: number, turns: number): RopeSegment => ({ type: 'burst', period, turns });
const d = (period: number, turns: number): RopeSegment => ({ type: 'double', period, turns });
const r = (period: number, turns: number): RopeSegment => ({ type: 'reverse', period, turns });

export const LEVELS: LevelDef[] = [
  { id: '1-1', world: 1, name: 'Primeros saltos', scene: 'pond', goalJumps: 10, fliesFor3: 1, rope: [s(1.55)] },
  { id: '1-2', world: 1, name: 'Al compás', scene: 'pond', goalJumps: 14, fliesFor3: 2, rope: [s(1.4, 6), s(1.3, 6)] },
  { id: '1-3', world: 1, name: 'Moscas al atardecer', scene: 'pond', goalJumps: 16, fliesFor3: 4, rope: [s(1.35)] },
  { id: '1-4', world: 1, name: 'Cada vez más rápido', scene: 'pond', goalJumps: 20, fliesFor3: 3, rope: [a(1.45, 1.1, 12), s(1.1, 8)] },
  { id: '1-5', world: 1, name: 'El gran estanque', scene: 'pond', goalJumps: 26, fliesFor3: 4, boss: true, rope: [s(1.25, 4), b(1.2, 5), f(1.15, 4)] },

  { id: '2-1', world: 2, name: 'Luces en el agua', scene: 'swamp', goalJumps: 14, fliesFor3: 2, rope: [s(1.35)] },
  { id: '2-2', world: 2, name: 'Dos cuerdas', scene: 'swamp', goalJumps: 16, fliesFor3: 2, rope: [s(1.3, 3), d(2.1, 6)] },
  { id: '2-3', world: 2, name: 'Amagos', scene: 'swamp', goalJumps: 25, fliesFor3: 5, rope: [s(1.2, 10), f(1.2, 4), d(1.9, 8)] },
  { id: '2-4', world: 2, name: 'Al revés', scene: 'swamp', goalJumps: 22, fliesFor3: 3, rope: [r(1.2, 6), a(1.25, 1.0, 8)] },
  { id: '2-5', world: 2, name: 'Rey del pantano', scene: 'swamp', goalJumps: 30, fliesFor3: 5, boss: true, rope: [d(1.9, 5), f(1.1, 4), b(1.1, 5), r(1.05, 4)] },

  { id: '3-1', world: 3, name: 'Primeras gotas', scene: 'storm', goalJumps: 16, fliesFor3: 2, rope: [s(1.35)] },
  { id: '3-2', world: 3, name: 'Viento cruzado', scene: 'storm', goalJumps: 20, fliesFor3: 3, rope: [a(1.35, 1.05, 10), s(1.05, 6)] },
  { id: '3-3', world: 3, name: 'Ráfagas', scene: 'storm', goalJumps: 22, fliesFor3: 4, rope: [b(1.2, 10)] },
  { id: '3-4', world: 3, name: 'Doble tormenta', scene: 'storm', goalJumps: 24, fliesFor3: 4, rope: [d(2.0, 6), s(1.1, 4)] },
  { id: '3-5', world: 3, name: 'Ojo de la tormenta', scene: 'storm', goalJumps: 32, fliesFor3: 6, boss: true, rope: [b(1.1, 5), d(1.8, 5), f(1.05, 4), a(1.1, 0.9, 8)] },

  { id: '4-1', world: 4, name: 'Hielo fino', scene: 'ice', goalJumps: 16, fliesFor3: 2, rope: [s(1.35)] },
  { id: '4-2', world: 4, name: 'Patinaje', scene: 'ice', goalJumps: 20, fliesFor3: 3, rope: [a(1.35, 1.05, 10)] },
  { id: '4-3', world: 4, name: 'Ventisca', scene: 'ice', goalJumps: 22, fliesFor3: 4, rope: [f(1.15, 6), s(1.05, 6)] },
  { id: '4-4', world: 4, name: 'Escarcha doble', scene: 'ice', goalJumps: 24, fliesFor3: 4, rope: [d(1.95, 6), r(1.1, 6)] },
  { id: '4-5', world: 4, name: 'La garza', scene: 'heron', goalJumps: 36, fliesFor3: 6, boss: true, rope: [s(1.1, 4), f(1.05, 4), d(1.8, 6), b(1.05, 5), r(1.0, 4)] },
];

export function levelById(id: string | undefined): LevelDef | undefined {
  return LEVELS.find((l) => l.id === id);
}

export function nextLevelId(id: string): string | undefined {
  const i = LEVELS.findIndex((l) => l.id === id);
  return i >= 0 && i + 1 < LEVELS.length ? LEVELS[i + 1].id : undefined;
}

export function totalTurns(segments: RopeSegment[]): number {
  return segments.reduce((n, sg) => n + sg.turns, 0);
}
